import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Alert, AlertDescription } from 'components/ui/alert';
import { Loader2, AlertCircle, CheckCircle2, Info } from 'lucide-react';
import { usePreviewQuantityChange, useUpdateQuantity } from 'features/subscription/hooks/use-subscription';

interface ManageSeatsDialogProps {
    open: boolean;
    onClose: () => void;
    subscriptionId: string;
    currentQuantity: number;
    minQuantity: number;
    maxQuantity: number | null;
    itemKey: string;
    version: number;
    currency?: string;
}

export function ManageSeatsDialog({
    open,
    onClose,
    subscriptionId,
    currentQuantity,
    minQuantity,
    maxQuantity,
    itemKey,
    version,
    currency = 'CHF'
}: ManageSeatsDialogProps) {
    const { t } = useTranslation();
    const [targetQuantity, setTargetQuantity] = useState(currentQuantity);
    const [showPreview, setShowPreview] = useState(false);

    React.useEffect(() => {
        if (open) {
            setTargetQuantity(currentQuantity);
            setShowPreview(false);
        }
    }, [open, currentQuantity]);

    const previewMutation = usePreviewQuantityChange(subscriptionId, {
        onSuccess: () => {
            setShowPreview(true);
        },
    });

    const updateMutation = useUpdateQuantity(subscriptionId, {
        onSuccess: () => {
            onClose();
            setShowPreview(false);
        },
    });

    const handlePreview = () => {
        if (targetQuantity === currentQuantity) {
            return;
        }

        previewMutation.mutate({
            version,
            quantities: [{ itemKey, quantity: targetQuantity }]
        });
    };

    const handleConfirm = () => {
        updateMutation.mutate({
            version,
            quantities: [{ itemKey, quantity: targetQuantity }]
        });
    };

    const handleClose = () => {
        setShowPreview(false);
        setTargetQuantity(currentQuantity);
        onClose();
    };

    const previewData = previewMutation.data;
    const isIncreasing = targetQuantity > currentQuantity;
    const isDecreasing = targetQuantity < currentQuantity;
    const quantityDiff = Math.abs(targetQuantity - currentQuantity);
    const isOutOfRange =
        targetQuantity < minQuantity ||
        (maxQuantity != null && targetQuantity > maxQuantity);
    const rangeLabel =
        maxQuantity != null ? `${minQuantity}-${maxQuantity}` : `${minQuantity}+`;

    const clampQuantity = (value: number) => {
        const next = Number.isFinite(value) ? value : minQuantity;
        const lower = Math.max(minQuantity, next);
        return maxQuantity != null ? Math.min(maxQuantity, lower) : lower;
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="text-foreground">{t('MANAGE_TEAM_SEATS', 'Manage Team Seats')}</DialogTitle>
                    <DialogDescription className="text-muted-foreground dark:text-neutral-300">
                        {t('ADJUST_NUMBER_OF_SEATS', `Adjust the number of seats for your team (${rangeLabel})`)}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 py-4 text-foreground">
                    {/* Current Seats Display */}
                    <div className="rounded-lg border border-border bg-muted/40 p-4">
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground dark:text-neutral-300">
                                {t('CURRENT_SEATS', 'Current Seats')}
                            </span>
                            <span className="text-lg font-semibold text-foreground">{currentQuantity}</span>
                        </div>
                    </div>

                    {/* Quantity Input */}
                    <div className="space-y-2">
                        <Label htmlFor="quantity" className="text-foreground">
                            {t('NEW_QUANTITY', 'Want more seats? Enter the number of seats you want to add or remove')}
                        </Label>
                        <Input
                            id="quantity"
                            type="number"
                            min={minQuantity}
                            max={maxQuantity ?? undefined}
                            value={targetQuantity}
                            onChange={(e) => {
                                setTargetQuantity(clampQuantity(parseInt(e.target.value, 10)));
                                setShowPreview(false);
                            }}
                            disabled={previewMutation.isPending || updateMutation.isPending}
                            className="h-12 text-lg text-foreground"
                        />
                        <p className="text-xs text-muted-foreground dark:text-neutral-300">
                            {isIncreasing && `Adding ${quantityDiff} seat${quantityDiff > 1 ? 's' : ''}`}
                            {isDecreasing && `Removing ${quantityDiff} seat${quantityDiff > 1 ? 's' : ''}`}
                            {targetQuantity === currentQuantity && 'No change'}
                        </p>
                    </div>

                    {isDecreasing && !isOutOfRange ? (
                        <Alert className="border-border bg-muted/50 text-foreground">
                            <Info className="h-4 w-4" />
                            <AlertDescription>
                                {t(
                                    'SEAT_DECREASE_TAKES_EFFECT_AT_PERIOD_END',
                                    'The seat decrease will take effect at the end of the billing period — there\'s no immediate impact.'
                                )}
                            </AlertDescription>
                        </Alert>
                    ) : null}

                    {/* Validation Errors */}
                    {isOutOfRange ? (
                        <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>
                                {t('SEATS_RANGE_ERROR', `Seats must be between ${rangeLabel}`)}
                            </AlertDescription>
                        </Alert>
                    ) : null}

                    {/* Preview Results */}
                    {showPreview && previewData && (
                        <div className="space-y-3 rounded-lg border border-border bg-muted/50 p-4">
                            <div className="flex items-start gap-2">
                                <CheckCircle2 className="mt-0.5 h-5 w-5 text-foreground" />
                                <div className="flex-1 space-y-2">
                                    <h4 className="text-sm font-semibold text-foreground">
                                        {t('PREVIEW_RESULTS', 'Preview Results')}
                                    </h4>

                                    <div className="space-y-1.5 text-sm">
                                        <div className="flex justify-between gap-4">
                                            <span className="text-muted-foreground dark:text-neutral-300">
                                                {t('TIMING', 'Timing')}:
                                            </span>
                                            <span className="font-medium text-foreground">
                                                {previewData.timing}
                                            </span>
                                        </div>

                                        {previewData.proratedChargeMinor > 0 && (
                                            <div className="flex justify-between gap-4">
                                                <span className="text-muted-foreground dark:text-neutral-300">
                                                    {t('DUE_NOW', 'Due Now')}:
                                                </span>
                                                <span className="font-bold text-foreground">
                                                    {(previewData.currencyCode || currency)} {(previewData.proratedChargeMinor / 100).toFixed(2)}
                                                </span>
                                            </div>
                                        )}

                                        <div className="flex justify-between gap-4">
                                            <span className="text-muted-foreground dark:text-neutral-300">
                                                {t('NEXT_RENEWAL', 'Next Renewal')}:
                                            </span>
                                            <span className="font-medium text-foreground">
                                                {(previewData.currencyCode || currency)} {(previewData.nextRenewalAmountMinor / 100).toFixed(2)}
                                            </span>
                                        </div>

                                        {previewData.taxAmountMinor > 0 && (
                                            <div className="flex justify-between gap-4 text-xs">
                                                <span className="text-muted-foreground dark:text-neutral-300">
                                                    {t('TAX_INCLUDED', 'Tax Included')}:
                                                </span>
                                                <span className="text-foreground">
                                                    {(previewData.currencyCode || currency)} {(previewData.taxAmountMinor / 100).toFixed(2)}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Error Message */}
                    {(previewMutation.isError || updateMutation.isError) && (
                        <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>
                                {previewMutation.error?.error?.message || updateMutation.error?.error?.message || t('OPERATION_FAILED')}
                            </AlertDescription>
                        </Alert>
                    )}
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        variant="outline"
                        onClick={handleClose}
                        disabled={previewMutation.isPending || updateMutation.isPending}
                    >
                        {t('CANCEL')}
                    </Button>

                    {!showPreview ? (
                        <Button
                            onClick={handlePreview}
                            disabled={
                                targetQuantity === currentQuantity ||
                                isOutOfRange ||
                                previewMutation.isPending
                            }
                        >
                            {previewMutation.isPending && (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            )}
                            {t('PREVIEW_CHANGES', 'Preview Changes')}
                        </Button>
                    ) : (
                        <Button
                            onClick={handleConfirm}
                            disabled={updateMutation.isPending}
                        >
                            {updateMutation.isPending && (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            )}
                            {t('CONFIRM_UPDATE', 'Confirm & Update')}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
